import { useCallback, useRef, useEffect } from 'react';

/**
 * Hook useSound — sons générés via Web Audio API (pas de fichiers externes)
 * Gère le déblocage automatique de l'AudioContext au premier geste utilisateur
 */
function useSound() {
  const audioCtxRef = useRef(null);
  const unlockedRef = useRef(false);

  // Crée ou retourne l'AudioContext existant
  const getCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtxRef.current;
  }, []);

  // Débloque l'AudioContext au premier clic/toucher utilisateur
  useEffect(() => {
    const unlock = () => {
      if (unlockedRef.current) return;
      const ctx = getCtx();
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => { unlockedRef.current = true; });
      } else {
        unlockedRef.current = true;
      }
    };
    window.addEventListener('click',     unlock, { once: false });
    window.addEventListener('touchstart', unlock, { once: false });
    window.addEventListener('keydown',   unlock, { once: false });
    return () => {
      window.removeEventListener('click',     unlock);
      window.removeEventListener('touchstart', unlock);
      window.removeEventListener('keydown',   unlock);
    };
  }, [getCtx]);

  // Helper : crée un oscillateur simple
  const playTone = useCallback((type, freqStart, freqEnd, duration, volume = 0.25) => {
    try {
      const ctx = getCtx();
      if (ctx.state === 'suspended') ctx.resume();
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = type;
      osc.frequency.setValueAtTime(freqStart, ctx.currentTime);
      if (freqEnd !== freqStart) {
        osc.frequency.exponentialRampToValueAtTime(freqEnd, ctx.currentTime + duration * 0.8);
      }
      gain.gain.setValueAtTime(volume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration);
    } catch (_) {}
  }, [getCtx]);

  // Correct — arpège joyeux Do-Mi-Sol
  const playCorrect = useCallback(() => {
    try {
      const ctx = getCtx();
      if (ctx.state === 'suspended') ctx.resume();
      [[523, 0], [659, 0.1], [784, 0.2]].forEach(([freq, delay]) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t = ctx.currentTime + delay;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.28, t + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
        osc.start(t);
        osc.stop(t + 0.22);
      });
    } catch (_) {}
  }, [getCtx]);

  // Incorrect — descente grave
  const playIncorrect = useCallback(() => {
    playTone('sawtooth', 280, 140, 0.35, 0.18);
  }, [playTone]);

  // Tick timer — bip court
  const playTick = useCallback(() => {
    playTone('square', 900, 900, 0.06, 0.07);
  }, [playTone]);

  // Victoire — fanfare Do Mi Sol Do (octave)
  const playVictory = useCallback(() => {
    try {
      const ctx = getCtx();
      if (ctx.state === 'suspended') ctx.resume();
      [
        [523, 0],
        [659, 0.13],
        [784, 0.26],
        [1047, 0.39],
        [784, 0.52],
        [1047, 0.62],
      ].forEach(([freq, delay]) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t = ctx.currentTime + delay;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.3, t + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
        osc.start(t);
        osc.stop(t + 0.28);
      });
    } catch (_) {}
  }, [getCtx]);

  // Click bouton — léger pop
  const playClick = useCallback(() => {
    playTone('sine', 620, 620, 0.07, 0.09);
  }, [playTone]);

  // Level up — glissando montant
  const playLevelUp = useCallback(() => {
    try {
      const ctx = getCtx();
      if (ctx.state === 'suspended') ctx.resume();
      [
        [400, 0],
        [600, 0.1],
        [800, 0.2],
        [1200, 0.3],
      ].forEach(([freq, delay]) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'triangle';
        osc.frequency.value = freq;
        const t = ctx.currentTime + delay;
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.25, t + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
        osc.start(t);
        osc.stop(t + 0.25);
      });
    } catch (_) {}
  }, [getCtx]);

  return { playCorrect, playIncorrect, playTick, playVictory, playClick, playLevelUp };
}

export default useSound;
